// apps/dashboard/src/hooks/use-websocket.ts
// Native WebSocket with automatic reconnect (exponential backoff, max 30 s).
// Connects to: ws(s)://<api-gateway>/ws?token=<JWT>
// Incoming events invalidate the relevant TanStack Query keys.

import { queryClient } from '@/lib/query-client';
import { useAuthStore } from '@/stores/auth.store';
import { useInboxStore } from '@/stores/inbox.store';
import { useNotificationStore } from '@/stores/notification.store';
import { useWsStore } from '@/stores/ws.store';
import { useEffect, useRef } from 'react';

// Grab WS store actions outside component to avoid re-render churn
function getWsActions() {
  return useWsStore.getState();
}

const WS_BASE =
  (import.meta.env.VITE_WS_URL as string | undefined) ??
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;

interface DomainEvent {
  type: string;
  payload: any;
  timestamp: number;
  aggregateId?: string;
}

export function useWebSocket() {
  const token = useAuthStore((s) => s.token);
  const setStatus = useWsStore((s) => s.setStatus);
  const addNotification = useNotificationStore((s) => s.addNotification);
  // Use individual selectors so the hook isn't re-rendered for unrelated
  // inbox-store changes (typing indicators, viewers, etc.). Destructuring
  // `useInboxStore()` would subscribe to the whole state slice and trigger
  // a re-run of this effect on every inbox mutation — which combined with
  // the WS event handlers can produce an infinite render loop.
  const addOrUpdateTicket = useInboxStore((s) => s.addOrUpdateTicket);
  const addMessage = useInboxStore((s) => s.addMessage);
  const updateTicket = useInboxStore((s) => s.updateTicket);
  const selectedTicketId = useInboxStore((s) => s.selectedTicketId);
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryDelayRef = useRef(1000);
  const presenceIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Ref para selectedTicketId — permite leitura dentro do effect sem re-executar o WS
  const selectedTicketIdRef = useRef(selectedTicketId);
  useEffect(() => {
    selectedTicketIdRef.current = selectedTicketId;
  }, [selectedTicketId]);

  useEffect(() => {
    if (!token) return;

    // Listen for custom send events from other hooks (like typing status)
    const handleCustomSend = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(detail));
      }
    };
    window.addEventListener('omniflow:ws:send', handleCustomSend);

    // Start presence heartbeat
    presenceIntervalRef.current = setInterval(() => {
      const ticketId = selectedTicketIdRef.current;
      if (wsRef.current?.readyState === WebSocket.OPEN && ticketId) {
        wsRef.current.send(
          JSON.stringify({
            type: 'CONVERSATION_VIEW_HEARTBEAT',
            conversationId: ticketId,
          }),
        );
      }
    }, 20000);

    function connect() {
      setStatus('connecting');
      const ws = new WebSocket(`${WS_BASE}/ws?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('connected');
        retryDelayRef.current = 1000; // reset backoff

        // If a ticket is already selected, send join event immediately
        const ticketId = selectedTicketIdRef.current;
        if (ticketId) {
          ws.send(
            JSON.stringify({
              type: 'CONVERSATION_VIEW_START',
              conversationId: ticketId,
            }),
          );
        }
      };

      ws.onmessage = (evt) => {
        try {
          const event = JSON.parse(evt.data) as DomainEvent;

          // Handle server-only message types (not DomainEvents)
          const rawMessage = JSON.parse(evt.data);
          if (rawMessage.type === 'PRESENCE_UPDATE') {
            const { conversationId: ticketId, viewing, typing } = rawMessage;
            if (ticketId) {
              updateTicket(ticketId, {
                viewingAgents: viewing,
                typingAgents: typing,
              });
            }
            return;
          }

          // Handle domain events from backend
          switch (event.type) {
            case 'TicketAssigned': {
              const {
                conversationId,
                agentId,
                id: ticketId,
                contactName,
                priority,
              } = event.payload;
              const currentUser = useAuthStore.getState().user;

              queryClient.invalidateQueries({ queryKey: ['conversations'] });

              // Only notify if assigned to the current user
              if (agentId === currentUser?.id) {
                // Atualizar Inbox Store V2
                addOrUpdateTicket({
                  id: ticketId || conversationId,
                  conversationId,
                  contactName: contactName || 'Novo Cliente',
                  status: 'OPEN',
                  priority: priority || 'MEDIUM',
                  lastMessageAt: new Date().toISOString(),
                  unreadCount: 1,
                  lastMessage: 'Você foi atribuído a este atendimento.',
                });

                addNotification({
                  type: 'ticket_assigned',
                  title: 'Novo Ticket Atribuído!',
                  message: `Ticket de ${contactName || 'desconhecido'} atribuído a você.`,
                  data: { conversationId },
                });
              }
              break;
            }
            case 'MessageSaved':
            case 'MessageReceived': {
              const conversationId = event.payload.conversationId as string;
              const { message, ticketId } = event.payload;

              queryClient.invalidateQueries({ queryKey: ['conversations'] });
              if (conversationId) {
                queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
                queryClient.invalidateQueries({ queryKey: ['conversations', conversationId] });
              }

              // Atualizar Inbox Store V2 se houver um ticket associado
              if (ticketId && message) {
                addMessage(ticketId, {
                  id: message.id,
                  ticketId,
                  content: message.content,
                  sender: message.direction === 'INBOUND' ? 'customer' : 'operator',
                  timestamp: message.createdAt,
                  type: message.contentType || 'text',
                });

                updateTicket(ticketId, {
                  lastMessage: message.content,
                  lastMessageAt: message.createdAt,
                });
              }
              break;
            }
            case 'ConversionDetected': {
              const { contactId, amount, currency, metadata } = event.payload;

              addNotification({
                type: 'conversion_detected',
                title: '💰 Venda Detectada via IA!',
                message: `Uma conversão de ${currency || 'BRL'} ${amount} foi detectada no chat.`,
                data: { contactId, amount, metadata },
              });

              // Invalida analytics para atualizar o dashboard de ROI em tempo real
              queryClient.invalidateQueries({ queryKey: ['analytics', 'roi'] });
              queryClient.invalidateQueries({ queryKey: ['conversions'] });
              break;
            }
            case 'ConversationStatusChanged': {
              const conversationId = event.payload.conversationId as string;
              queryClient.invalidateQueries({ queryKey: ['conversations'] });
              if (conversationId) {
                queryClient.invalidateQueries({ queryKey: ['conversations', conversationId] });
              }
              break;
            }
            case 'ConversationAssigned': {
              // Optimistically update assignedToUserId in the infinite list
              const convId = event.payload.conversationId as string;
              const userId = event.payload.userId as string | null;

              type Page = { data: Array<Record<string, unknown>>; page: number; limit: number };
              type InfiniteData = { pages: Page[]; pageParams: unknown[] };

              queryClient.setQueriesData<InfiniteData>(
                { queryKey: ['conversations'], exact: false },
                (old) => {
                  if (!old) return old;
                  return {
                    ...old,
                    pages: old.pages.map((page) => ({
                      ...page,
                      data: page.data.map((c) =>
                        c.id !== convId ? c : { ...c, assignedToUserId: userId },
                      ),
                    })),
                  };
                },
              );
              queryClient.invalidateQueries({ queryKey: ['conversations', convId] });
              break;
            }
            case 'ConversationRead': {
              // Optimistically zero the unread badge so it disappears instantly
              const convId = event.payload.conversationId as string;

              type Page = { data: Array<Record<string, unknown>>; page: number; limit: number };
              type InfiniteData = { pages: Page[]; pageParams: unknown[] };

              queryClient.setQueriesData<InfiniteData>(
                { queryKey: ['conversations'], exact: false },
                (old) => {
                  if (!old) return old;
                  return {
                    ...old,
                    pages: old.pages.map((page) => ({
                      ...page,
                      data: page.data.map((c) => (c.id !== convId ? c : { ...c, unreadCount: 0 })),
                    })),
                  };
                },
              );
              queryClient.invalidateQueries({ queryKey: ['conversations', convId] });
              break;
            }
            case 'pong':
              // Heartbeat response from server
              break;
            case 'CampaignMessageSent':
            case 'CampaignStatusChanged': {
              const campaignId = event.payload.campaignId || event.payload.id;
              // Invalida a lista para pegar estatísticas novas
              queryClient.invalidateQueries({ queryKey: ['campaigns'] });
              if (campaignId) {
                queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] });
              }
              break;
            }
            case 'EmailOpenedTracked': {
              // EPIC 1 / OMNI-2026-004: força refresh imediato das métricas de campanha
              // quando o pixel de abertura é processado no backend.
              queryClient.invalidateQueries({ queryKey: ['campaigns'] });
              break;
            }
            case 'WaQrCodeGenerated': {
              const phoneId = event.payload.phoneId as string;
              const qrBase64 = event.payload.qrBase64 as string;
              const expiresAt = event.payload.expiresAt as string;
              const attempt = event.payload.attempt as number;
              getWsActions().setQrEvent(phoneId, { phoneId, qrBase64, expiresAt, attempt });
              // Invalidate wa-sessions list so status badge updates
              queryClient.invalidateQueries({ queryKey: ['wa-sessions'] });
              break;
            }
            case 'WaSessionConnected': {
              const phoneId = event.payload.phoneId as string;
              getWsActions().setSessionStatus(phoneId, 'CONNECTED');
              getWsActions().clearQrEvent(phoneId);
              queryClient.invalidateQueries({ queryKey: ['wa-sessions'] });
              break;
            }
            case 'WaSessionDisconnected': {
              const phoneId = event.payload.phoneId as string;
              getWsActions().setSessionStatus(phoneId, 'DISCONNECTED');
              getWsActions().clearQrEvent(phoneId);
              queryClient.invalidateQueries({ queryKey: ['wa-sessions'] });
              break;
            }
            case 'NotificationReceived': {
              // Invalidate notification center — badge and list refresh instantly
              queryClient.invalidateQueries({ queryKey: ['notifications'] });
              break;
            }
            case 'MENTION_RECEIVED': {
              // Real-time @mention — invalidate mentions query for instant badge update
              queryClient.invalidateQueries({ queryKey: ['mentions'] });
              queryClient.invalidateQueries({ queryKey: ['notifications'] });
              break;
            }
            case 'CUSTOMER_TYPING': {
              // QW-07 — "Cliente está digitando..." real-time indicator
              const { contactPhone, inboxId, isTyping } = event as unknown as {
                contactPhone: string;
                inboxId: string | null;
                isTyping: boolean;
              };
              if (!contactPhone) break;
              const { setCustomerTyping } = useInboxStore.getState();
              setCustomerTyping(contactPhone, inboxId, isTyping);
              // Auto-clear after 8 s in case the wa-unofficial stop event is missed
              if (isTyping) {
                setTimeout(() => setCustomerTyping(contactPhone, inboxId, false), 8_000);
              }
              break;
            }
            case 'SlaBreached': {
              // H1-02 — atualiza badge SLA no ConversationCard imediatamente
              const breachedConvId = event.payload.conversationId as string;
              if (breachedConvId) {
                type Page = { data: Array<Record<string, unknown>>; page: number; limit: number };
                type InfiniteData = { pages: Page[]; pageParams: unknown[] };
                queryClient.setQueriesData<InfiniteData>(
                  { queryKey: ['conversations'], exact: false },
                  (old) => {
                    if (!old) return old;
                    return {
                      ...old,
                      pages: old.pages.map((page) => ({
                        ...page,
                        data: page.data.map((c) =>
                          c.id !== breachedConvId ? c : { ...c, slaBreached: true },
                        ),
                      })),
                    };
                  },
                );
                updateTicket(breachedConvId, { slaStatus: 'BREACHED' });
                queryClient.invalidateQueries({ queryKey: ['conversations', breachedConvId] });
                addNotification({
                  type: 'sla_breached',
                  title: 'SLA Estourado',
                  message: `Conversa ${breachedConvId.slice(0, 8)} ultrapassou o prazo de SLA.`,
                  data: { conversationId: breachedConvId },
                });
              }
              break;
            }
            case 'SlaWarning': {
              // H1-02 — aviso de SLA em risco (dentro do threshold de warning)
              const warnConvId = event.payload.conversationId as string;
              if (warnConvId) {
                updateTicket(warnConvId, { slaStatus: 'WARNING' });
                queryClient.invalidateQueries({ queryKey: ['conversations', warnConvId] });
              }
              break;
            }
            case 'PriorityChanged': {
              // H1-01 — atualiza badge de prioridade em tempo real sem reload
              const convId = event.payload.conversationId as string;
              const newPriority = event.payload.newPriority as string;
              if (!convId || !newPriority) break;

              type Page = { data: Array<Record<string, unknown>>; page: number; limit: number };
              type InfiniteData = { pages: Page[]; pageParams: unknown[] };
              queryClient.setQueriesData<InfiniteData>(
                { queryKey: ['conversations'], exact: false },
                (old) => {
                  if (!old) return old;
                  return {
                    ...old,
                    pages: old.pages.map((page) => ({
                      ...page,
                      data: page.data.map((c) =>
                        c.id !== convId ? c : { ...c, priority: newPriority },
                      ),
                    })),
                  };
                },
              );
              updateTicket(convId, {
                priority: newPriority as 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT',
              });
              queryClient.invalidateQueries({ queryKey: ['conversations', convId] });
              break;
            }
            case 'ConversationCreated': {
              queryClient.invalidateQueries({ queryKey: ['conversations'] });
              break;
            }
            case 'ConversationUpdated': {
              const convId = event.payload.conversationId as string;
              const preview = event.payload.lastMessagePreview as string | undefined;
              const lastMsgAt = event.payload.lastMessageAt as string | undefined;

              if (!convId) break;

              // ALWAYS invalidate messages so the chat window refreshes without F5.
              // Previously this was inside an `if (isSelected)` block that relied on
              // `allTickets` being populated — if the store was empty the messages
              // query was never invalidated and users had to manually refresh.
              queryClient.invalidateQueries({ queryKey: ['messages', convId] });

              // Determine if this conversation is currently visible
              const isSelected = useInboxStore.getState().selectedTicketId === convId;

              type Page = { data: Array<Record<string, unknown>>; page: number; limit: number };
              type InfiniteData = { pages: Page[]; pageParams: unknown[] };

              queryClient.setQueriesData<InfiniteData>(
                { queryKey: ['conversations'], exact: false },
                (old) => {
                  if (!old) return old;
                  return {
                    ...old,
                    pages: old.pages.map((page) => ({
                      ...page,
                      data: page.data.map((c) => {
                        if (c.id !== convId) return c;
                        return {
                          ...c,
                          // Only increment unread when the conversation is NOT open
                          ...(!isSelected
                            ? { unreadCount: ((c.unreadCount as number) ?? 0) + 1 }
                            : {}),
                          ...(preview !== undefined ? { lastMessagePreview: preview } : {}),
                          ...(lastMsgAt !== undefined ? { lastMessageAt: lastMsgAt } : {}),
                        };
                      }),
                    })),
                  };
                },
              );

              if (isSelected) {
                // Mark as read silently (fire-and-forget)
                import('@/lib/api').then(({ api }) => {
                  api.post(`/conversations/${convId}/read`, {}).catch(() => {});
                });
              }
              break;
            }
            default:
              // Unknown event type - log to console in dev
              if (import.meta.env.DEV) {
                console.log('[WebSocket] Unknown event type:', event.type, event);
              }
          }
        } catch (err) {
          // Ignore malformed messages
          if (import.meta.env.DEV) {
            console.warn('[WebSocket] Failed to parse message:', evt.data, err);
          }
        }
      };

      ws.onclose = () => {
        setStatus('disconnected');
        // Reconnect with exponential backoff (cap at 30 s)
        const delay = Math.min(retryDelayRef.current, 30_000);
        retryDelayRef.current = Math.min(delay * 2, 30_000);
        retryRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        setStatus('error');
        ws.close();
      };
    }

    connect();

    return () => {
      window.removeEventListener('omniflow:ws:send', handleCustomSend);
      if (retryRef.current) clearTimeout(retryRef.current);
      if (presenceIntervalRef.current) clearInterval(presenceIntervalRef.current);
      wsRef.current?.close();
      setStatus('disconnected');
    };
    // Reconecta apenas quando token muda (login/logout) — selectedTicketId via ref.
    // Zustand actions (addNotification, addOrUpdateTicket, addMessage, updateTicket)
    // are stable references, so listing them as deps does not cause the
    // WebSocket connection to be torn down on unrelated re-renders.
  }, [token, setStatus, addNotification, addOrUpdateTicket, addMessage, updateTicket]);
}
